import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/hen-ui/utils';
import { cardVariants, type CardVariantProps } from './card.variants';

/**
 * A flexible card component for displaying grouped content.
 *
 * Supports compound component pattern with Card.Content and Card.Footer subcomponents.
 *
 * @example
 * ```tsx
 * <Card variant="background-white">
 *   <Card.Content>
 *     <h3>Card Title</h3>
 *     <p>Card content</p>
 *   </Card.Content>
 *   <Card.Footer>
 *     <span>Footer text</span>
 *   </Card.Footer>
 * </Card>
 * ```
 */
export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    CardVariantProps {
  asChild?: boolean;
}

type CardComponent = typeof Card & {
  Content: typeof CardContent;
  Footer: typeof CardFooter;
};

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    { className, variant, border, asChild = false, children, ...props },
    ref,
  ) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        ref={ref}
        className={cn(cardVariants({ variant, border }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  },
);
Card.displayName = 'Card';

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        ref={ref}
        className={cn(
          'flex flex-col gap-sm py-7 px-8 w-full self-start',
          className,
        )}
        {...props}
      />
    );
  },
);
CardContent.displayName = 'CardContent';

// TODO: Revisit CardFooter spacing - currently tied to Card's py-7 px-8 padding and a width calculation that needs to be adjusted
const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        ref={ref}
        className={cn(
          'flex items-center justify-between bg-secondary-50 gap-lg py-d3 px-8 mt-auto rounded-b-lg w-full [&>*:first-child]:grow',
          className,
        )}
        {...props}
      />
    );
  },
);
CardFooter.displayName = 'CardFooter';

const CardWithSubcomponents = Card as CardComponent;
CardWithSubcomponents.Content = CardContent;
CardWithSubcomponents.Footer = CardFooter;

export { CardWithSubcomponents as Card, CardContent, CardFooter };
